import { Link } from 'react-router-dom';
import { useState } from 'react';
import { faqs, testimonials } from '@/mocks/manga';

export default function About() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMsg, setFormMsg] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMsg('提交成功！我们会尽快回复您。');
      form.reset();
      return;
    }

    try {
      const body = new URLSearchParams();
      body.append('name', (formData.get('name') as string) || '');
      body.append('email', (formData.get('email') as string) || '');
      body.append('message', (formData.get('message') as string) || '');

      const response = await fetch('https://readdy.ai/api/form/d9pbv5iqs38sj6jc7lh0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const text = await response.text();
      if (response.ok) {
        let parsed = null;
        try { parsed = JSON.parse(text); } catch { /* ignore */ }
        if (parsed && parsed.code === 'OK') {
          setFormStatus('success');
          setFormMsg('感谢您的留言！我们会尽快回复。');
          form.reset();
        } else {
          setFormStatus('error');
          setFormMsg(parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试。');
        }
      } else {
        setFormStatus('error');
        setFormMsg('提交失败，请稍后再试。');
      }
    } catch {
      setFormStatus('error');
      setFormMsg('网络错误，请稍后再试。');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.yinghuadongman.com/" },
              { "@type": "ListItem", "position": 2, "name": "关于我们", "item": "https://www.yinghuadongman.com/about" }
            ]
          })
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "关于樱花动漫网",
            "description": "樱花动漫网是专注韩漫下拉式在线阅读的中文平台，提供海量免费韩漫资源。",
            "url": "https://www.yinghuadongman.com/about"
          })
        }}
      />

      {/* Page Header */}
      <section className="relative pt-24 pb-12 md:pb-16 px-4 md:px-6 bg-background-100/50" aria-labelledby="about-title">
        <div className="max-w-4xl mx-auto text-center">
          <nav className="flex items-center justify-center gap-2 text-sm text-foreground-400 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-600">关于我们</span>
          </nav>
          <h1 id="about-title" className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">关于樱花动漫网</h1>
          <p className="text-base md:text-lg text-foreground-500 max-w-2xl mx-auto leading-relaxed">
            致力于为中文读者提供最优质的韩漫下拉式在线阅读体验
          </p>
        </div>
      </section>

      {/* Brand Introduction */}
      <section className="py-12 md:py-16 px-4 md:px-6" aria-labelledby="brand-heading">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            <div className="flex-shrink-0 w-full md:w-1/2">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20illustration%20of%20a%20cozy%20reading%20nook%20with%20warm%20lighting%2C%20bookshelves%20filled%20with%20colorful%20manga%20volumes%2C%20soft%20atmospheric%20glow%2C%20cherry%20blossom%20view%20through%20window%2C%20digital%20art%20style%2C%20inviting%20and%20peaceful%20composition&width=800&height=600&seq=about-brand-01&orientation=landscape"
                  alt="樱花动漫网品牌形象"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="flex-1">
              <h2 id="brand-heading" className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">品牌介绍</h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                <p>
                  樱花动漫网成立于2024年，是一个<strong>专注韩漫下拉式在线阅读</strong>的中文漫画平台。我们的名字"樱花"代表着我们对美好事物的追求和对漫画艺术的热爱，我们希望每一位来到这里的读者，都能像欣赏春日樱花一样，享受阅读韩漫的愉悦体验。
                </p>
                <p>
                  平台汇聚了海量<strong>免费韩漫</strong>资源，涵盖恋爱、奇幻、动作、悬疑、日常、剧情、搞笑等多个分类。无论你是韩漫老粉还是新入坑的读者，都能在这里找到自己喜欢的作品。我们采用<strong>下拉式阅读</strong>技术，让你能够流畅地连续阅读，无需频繁翻页，在手机和电脑上都能获得极佳的阅读体验。
                </p>
                <p>
                  我们的团队由一群热爱韩漫的年轻人组成，包括经验丰富的编辑、技术精湛的工程师和充满创意的设计师。我们每天都在努力，只为给你带来更丰富的漫画内容和更优质的<strong>在线韩漫</strong>阅读服务。
                </p>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-8">
                {[
                  { value: '2024', label: '成立年份' },
                  { value: '12,000+', label: '韩漫资源' },
                  { value: '500万+', label: '月活读者' },
                ].map((item, i) => (
                  <div key={i} className="text-center p-4 rounded-xl bg-accent-100/50 border border-accent-200/50">
                    <div className="text-lg md:text-xl font-heading font-bold text-accent-700">{item.value}</div>
                    <div className="text-xs text-accent-600 mt-1">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reputation / Testimonials */}
      <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100/50" aria-labelledby="reputation-heading">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 id="reputation-heading" className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">读者口碑</h2>
            <p className="mt-2 text-sm text-foreground-500">数百万读者的真实评价</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.map((t) => (
              <div key={t.id} className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                <div className="flex items-center gap-3 mb-4">
                  <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                    <div className="text-xs text-foreground-400">{t.date}</div>
                  </div>
                </div>
                <div className="flex items-center gap-0.5 mb-3">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{t.comment}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-12 md:py-16 px-4 md:px-6" aria-labelledby="contact-heading">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h2 id="contact-heading" className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">联系我们</h2>
            <p className="mt-2 text-sm text-foreground-500">有任何问题或建议？欢迎给我们留言</p>
          </div>
          <form onSubmit={handleContactSubmit} data-readdy-form="true" className="space-y-5 p-6 md:p-8 rounded-2xl bg-background-50 border border-background-200/70">
            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="honeypot-field"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  required
                  placeholder="请输入您的姓名"
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-800 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 transition-all"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  required
                  placeholder="请输入您的邮箱"
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-800 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 transition-all"
                />
              </div>
            </div>
            <div>
              <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
              <textarea
                id="contact-message"
                name="message"
                required
                rows={5}
                maxLength={500}
                placeholder="请输入您的留言（最多500字）"
                className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-800 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 transition-all resize-none"
              ></textarea>
              <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
            </div>
            <button
              type="submit"
              className="w-full py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-all whitespace-nowrap cursor-pointer"
            >
              发送留言
            </button>
            {formStatus !== 'idle' && (
              <p className={`text-center text-sm ${formStatus === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                {formMsg}
              </p>
            )}
          </form>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100/50" aria-labelledby="about-faq-heading">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h2 id="about-faq-heading" className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">常见问题</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="rounded-xl border border-background-200/70 overflow-hidden">
                <button
                  className="w-full flex items-center justify-between p-5 text-left bg-background-50 hover:bg-background-100 transition-colors cursor-pointer"
                  onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                  aria-expanded={activeFaq === i}
                >
                  <span className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</span>
                  <i className={`ri-${activeFaq === i ? 'subtract' : 'add'}-line text-foreground-400 flex-shrink-0 text-lg`}></i>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${activeFaq === i ? 'max-h-60' : 'max-h-0'}`}>
                  <p className="px-5 pb-5 text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}